import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Upload, FileSpreadsheet, Loader2, FlaskConical, Download, Trash2, Sparkles,
} from "lucide-react";
import CreateDerivedMetricModal from "./CreateDerivedMetricModal";
import CreditCostTag from "../../CreditCostTag";

export function DataUploadTab({
  projectId, token,
  datasets, selectedDataset, setSelectedDataset,
  uploading, handleFileUpload, exportDataset,
  derivedMetrics, showCreateDerivedModal, setShowCreateDerivedModal,
  fetchDerivedMetrics, deleteDerivedMetric,
  aiGenerating, aiDataCollectionPlan, aiMeasurementReview,
  reviewMeasurementSystem, generateDataCollectionPlan,
}) {
  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">Upload Dataset</h3>
        </div>
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <input type="file" id="file-upload" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
          <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-4">
            {uploading ? <Loader2 className="w-10 h-10 text-primary animate-spin" /> : <Upload className="w-10 h-10 text-muted-foreground" />}
            <div>
              <p className="font-medium">{uploading ? "Uploading..." : "Click to upload or drag and drop"}</p>
              <p className="text-sm text-muted-foreground">CSV or Excel files supported</p>
            </div>
          </label>
        </div>
      </div>

      {/* Datasets List */}
      {datasets.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Uploaded Datasets</h3>
            {selectedDataset && (
              <Button variant="outline" size="sm" onClick={exportDataset} className="gap-2">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {datasets.map((dataset) => (
              <div
                key={dataset.dataset_id}
                onClick={() => setSelectedDataset(dataset)}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${selectedDataset?.dataset_id === dataset.dataset_id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                data-testid={`dataset-${dataset.dataset_id}`}
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{dataset.filename}</p>
                    <p className="text-sm text-muted-foreground">{dataset.row_count} rows &bull; {dataset.columns.length} columns</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Profile */}
      {selectedDataset?.profile?.summary && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-semibold mb-4">Data Profile</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Rows", value: selectedDataset.profile.summary.total_rows },
              { label: "Columns", value: selectedDataset.profile.summary.total_columns },
              { label: "Missing Cells", value: selectedDataset.profile.summary.missing_cells ?? 0 },
              { label: "Missing %", value: `${selectedDataset.profile.summary.missing_percentage ?? 0}%` },
            ].map((stat) => (
              <div key={stat.label} className="bg-background border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="text-xl font-bold font-heading">{stat.value ?? "N/A"}</p>
              </div>
            ))}
          </div>
          {selectedDataset.profile.columns && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">Column</th>
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">Non-Null</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">Unique</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">Mean</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">Std</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(selectedDataset.profile.columns).map(([name, info]) => (
                    <tr key={name} className="border-b border-border">
                      <td className="py-2 px-4 font-medium">{name}</td>
                      <td className="py-2 px-4 text-muted-foreground">{info.dtype}</td>
                      <td className="py-2 px-4 text-right">{info.non_null}</td>
                      <td className="py-2 px-4 text-right">{info.unique_count}</td>
                      <td className="py-2 px-4 text-right">{info.mean ?? "-"}</td>
                      <td className="py-2 px-4 text-right">{info.std ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Derived Metrics Section */}
      {selectedDataset && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="derived-metrics-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold">Derived Metrics</h3>
              {derivedMetrics.length > 0 && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">{derivedMetrics.length}</span>
              )}
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowCreateDerivedModal(true)} data-testid="create-derived-metric-btn">
              <FlaskConical className="w-3.5 h-3.5" /> Create Derived Metric
            </Button>
          </div>
          {derivedMetrics.length === 0 ? (
            <p className="text-sm text-muted-foreground">No derived metrics yet. Create a rate-based metric (e.g., defects per unit) from your dataset columns.</p>
          ) : (
            <div className="space-y-2">
              {derivedMetrics.map((dm) => (
                <div key={dm.metric_id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg" data-testid={`derived-metric-${dm.metric_id}`}>
                  <div className="flex items-center gap-3">
                    <FlaskConical className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{dm.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {dm.numerator_column} / {dm.denominator_column}
                        {dm.type === "percent" && " x 100"}
                        {dm.type === "scaled_rate" && dm.multiplier && ` x ${dm.multiplier}`}
                        {dm.unit_label && ` (${dm.unit_label})`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">{dm.type}</span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => deleteDerivedMetric(dm.metric_id)} data-testid={`delete-derived-${dm.metric_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CreateDerivedMetricModal
        open={showCreateDerivedModal}
        onOpenChange={setShowCreateDerivedModal}
        projectId={projectId}
        dataset={selectedDataset}
        token={token}
        onCreated={() => fetchDerivedMetrics(selectedDataset?.dataset_id)}
      />

      {/* AI Data Collection Plan */}
      {selectedDataset && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm">AI: Data Collection Plan</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reviewMeasurementSystem} disabled={!!aiGenerating} className="gap-2" data-testid="ai-review-measurement-btn">
                {aiGenerating === "Measurement Review" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Review Data
                <CreditCostTag cost={5} />
              </Button>
              <Button variant="outline" size="sm" onClick={generateDataCollectionPlan} disabled={!!aiGenerating} className="gap-2" data-testid="ai-data-collection-plan-btn">
                {aiGenerating === "Data Collection Plan" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate Plan
                <CreditCostTag cost={5} />
              </Button>
            </div>
          </div>

          {/* Measurement Review Results */}
          {aiMeasurementReview && !aiMeasurementReview.ai_unavailable && (
            <div className="space-y-3" data-testid="ai-measurement-review-panel">
              <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <span className="text-sm font-medium">{aiMeasurementReview.overall_assessment}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">Score: {aiMeasurementReview.completeness_score}%</span>
              </div>
              {aiMeasurementReview.findings?.map((f, i) => (
                <div key={i} className={`p-2 rounded border text-xs ${f.severity === "high" ? "bg-red-500/10 border-red-500/20" : f.severity === "medium" ? "bg-amber-500/10 border-amber-500/20" : "bg-emerald-500/10 border-emerald-500/20"}`}>
                  <span className={`font-medium ${f.severity === "high" ? "text-red-400" : f.severity === "medium" ? "text-amber-400" : "text-emerald-400"}`}>[{f.severity}]</span> {f.finding}
                  <p className="text-muted-foreground mt-1">{f.recommendation}</p>
                </div>
              ))}
              {aiMeasurementReview.stratification_suggestions?.length > 0 && (
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Stratification Factors</p>
                  {aiMeasurementReview.stratification_suggestions.map((s, i) => (
                    <p key={i} className="text-xs p-2 bg-background border border-border rounded"><strong>{s.factor}:</strong> {s.rationale}</p>
                  ))}
                </div>
              )}
              {aiMeasurementReview.msa_recommendations?.length > 0 && (
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">MSA Recommendations</p>
                  {aiMeasurementReview.msa_recommendations.map((m, i) => (
                    <p key={i} className="text-xs p-2 bg-background border border-border rounded"><strong>{m.metric}:</strong> {m.concern} — <em>{m.method}</em></p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Data Collection Plan Results */}
          {aiDataCollectionPlan && !aiDataCollectionPlan.ai_unavailable && (
            <div className="space-y-3" data-testid="ai-data-collection-plan-panel">
              {aiDataCollectionPlan.metrics?.map((m, i) => (
                <div key={i} className="p-3 bg-background border border-border rounded-lg space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{m.metric_name}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary">{m.data_type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{m.definition}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>Sample: {m.sample_size}</span>
                    <span>Frequency: {m.frequency}</span>
                    <span>Method: {m.collection_method}</span>
                  </div>
                </div>
              ))}
              {aiDataCollectionPlan.operational_definitions?.length > 0 && (
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Operational Definitions</p>
                  {aiDataCollectionPlan.operational_definitions.map((od, i) => (
                    <div key={i} className="p-2 bg-background border border-border rounded text-xs">
                      <strong>{od.term}:</strong> {od.definition}
                      {od.example && <p className="text-muted-foreground mt-0.5">Example: {od.example}</p>}
                    </div>
                  ))}
                </div>
              )}
              {aiDataCollectionPlan.measurement_risks?.length > 0 && (
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Measurement Risks</p>
                  {aiDataCollectionPlan.measurement_risks.map((r, i) => (
                    <div key={i} className={`p-2 rounded border text-xs ${r.severity === "high" ? "bg-red-500/10 border-red-500/20" : r.severity === "medium" ? "bg-amber-500/10 border-amber-500/20" : "bg-emerald-500/10 border-emerald-500/20"}`}>
                      <strong>{r.risk}</strong>
                      <p className="text-muted-foreground">{r.mitigation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
